import { useEffect, useRef, useMemo } from 'react';
import signalRManager, { 
  SignalRSubscription, 
  QuestionStatusChangedHandler, 
  QuestionMemberHandler, 
  QuestionCreatedHandler, 
  CommentCreatedHandler, 
  CommentUpdatedHandler, 
  CommentDeletedHandler,
  SessionCreatedHandler,
  SessionUpdatedHandler,
  SessionClosedHandler,
  SessionDeletedHandler
} from '../services/signalRManager';

// Оптимизированный хук для SignalR подписок
export const useOptimizedSignalR = (
  handlers: {
    onQuestionStatusChanged?: QuestionStatusChangedHandler;
    onQuestionMemberAdded?: QuestionMemberHandler;
    onQuestionMemberRemoved?: QuestionMemberHandler;
    onQuestionCreated?: QuestionCreatedHandler;
    onCommentCreated?: CommentCreatedHandler;
    onCommentUpdated?: CommentUpdatedHandler;
    onCommentDeleted?: CommentDeletedHandler;
    onSessionCreated?: SessionCreatedHandler;
    onSessionUpdated?: SessionUpdatedHandler;
    onSessionClosed?: SessionClosedHandler;
    onSessionDeleted?: SessionDeletedHandler;
  },
  options: {
    siteId?: number;
    questionIds?: number[];
    subscriptionId?: string;
  } = {}
) => {
  const subscriptionIdRef = useRef<string | null>(null);
  const handlersRef = useRef(handlers);
  const prevHandlersKeyRef = useRef<string>('');

  // Создаем стабильный ID для подписки (убираем Date.now() для стабильности)
  const subscriptionId = useMemo(() => 
    options.subscriptionId || `subscription_${options.siteId || 'global'}`,
    [options.subscriptionId, options.siteId]
  );

  // Стабилизируем handlers - сравниваем по функциям, а не по ссылке объекта
  const handlersKey = useMemo(() => {
    const funcKeys = [
      handlers.onQuestionStatusChanged?.toString(),
      handlers.onQuestionMemberAdded?.toString(),
      handlers.onQuestionMemberRemoved?.toString(),
      handlers.onQuestionCreated?.toString(),
      handlers.onCommentCreated?.toString(),
      handlers.onCommentUpdated?.toString(),
      handlers.onCommentDeleted?.toString(),
      handlers.onSessionCreated?.toString(),
      handlers.onSessionUpdated?.toString(),
      handlers.onSessionClosed?.toString(),
      handlers.onSessionDeleted?.toString(),
    ].filter(Boolean).join('|');
    return funcKeys;
  }, [
    handlers.onQuestionStatusChanged,
    handlers.onQuestionMemberAdded,
    handlers.onQuestionMemberRemoved,
    handlers.onQuestionCreated,
    handlers.onCommentCreated,
    handlers.onCommentUpdated,
    handlers.onCommentDeleted,
    handlers.onSessionCreated,
    handlers.onSessionUpdated,
    handlers.onSessionClosed,
    handlers.onSessionDeleted,
  ]);

  // Используем ref для хранения стабильной ссылки на handlers
  const stableHandlers = useMemo(() => {
    // Проверяем, изменились ли функции
    if (handlersKey !== prevHandlersKeyRef.current) {
      prevHandlersKeyRef.current = handlersKey;
      handlersRef.current = handlers;
      return handlers;
    }
    return handlersRef.current;
  }, [handlersKey]);

  // Стабилизируем questionIds массив - сравниваем по содержимому, а не по ссылке
  const questionIdsKey = useMemo(() => 
    options.questionIds ? JSON.stringify([...options.questionIds].sort((a, b) => a - b)) : '',
    [options.questionIds]
  );
  
  const stableQuestionIds = useMemo(() => {
    if (!options.questionIds || options.questionIds.length === 0) {
      return undefined;
    }
    // Создаем отсортированную копию для стабильности
    return [...options.questionIds].sort((a, b) => a - b);
  }, [questionIdsKey]);

  // Регистрируем подписку
  useEffect(() => {
    const subscription: SignalRSubscription = {
      id: subscriptionId,
      handlers: stableHandlers,
      siteId: options.siteId,
      questionIds: stableQuestionIds,
    };

    subscriptionIdRef.current = signalRManager.subscribe(subscription);

    return () => {
      if (subscriptionIdRef.current) {
        signalRManager.unsubscribe(subscriptionIdRef.current);
        subscriptionIdRef.current = null;
      }
    };
  }, [subscriptionId, options.siteId, stableQuestionIds, stableHandlers]);

  // Возвращаем информацию о состоянии
  return {
    isConnected: signalRManager.connected,
    connectionState: signalRManager.connectionState,
    activeSubscriptions: signalRManager.activeSubscriptions,
    connectedGroupsCount: signalRManager.connectedGroupsCount,
  };
};

// Хук для подписки на события конкретного сайта
export const useSiteSignalR = (
  siteId: number,
  handlers: {
    onQuestionStatusChanged?: QuestionStatusChangedHandler;
    onQuestionMemberAdded?: QuestionMemberHandler;
    onQuestionMemberRemoved?: QuestionMemberHandler;
    onQuestionCreated?: QuestionCreatedHandler;
    onSessionCreated?: SessionCreatedHandler;
    onSessionUpdated?: SessionUpdatedHandler;
    onSessionClosed?: SessionClosedHandler;
    onSessionDeleted?: SessionDeletedHandler;
  }
) => {
  return useOptimizedSignalR(handlers, { 
    siteId, 
    subscriptionId: `site_${siteId}` 
  });
};

// Хук для подписки на события конкретного вопроса
export const useQuestionSignalR = (
  questionId: number,
  handlers: {
    onQuestionStatusChanged?: QuestionStatusChangedHandler;
    onQuestionMemberAdded?: QuestionMemberHandler;
    onQuestionMemberRemoved?: QuestionMemberHandler;
    onCommentCreated?: CommentCreatedHandler;
    onCommentUpdated?: CommentUpdatedHandler;
    onCommentDeleted?: CommentDeletedHandler;
  }
) => {
  // Всегда вызываем хук, но передаем пустой массив questionIds если questionId невалидный
  const validQuestionIds = (!questionId || questionId <= 0) ? [] : [questionId];
  
  return useOptimizedSignalR(handlers, { 
    questionIds: validQuestionIds, 
    subscriptionId: validQuestionIds.length > 0 ? `question_${questionId}` : undefined
  });
};

// Хук для подписки на события нескольких вопросов
export const useMultipleQuestionsSignalR = (
  questionIds: number[],
  handlers: {
    onQuestionStatusChanged?: QuestionStatusChangedHandler;
    onQuestionMemberAdded?: QuestionMemberHandler;
    onQuestionMemberRemoved?: QuestionMemberHandler;
    onCommentCreated?: CommentCreatedHandler;
    onCommentUpdated?: CommentUpdatedHandler;
    onCommentDeleted?: CommentDeletedHandler;
  }
) => {
  return useOptimizedSignalR(handlers, { 
    questionIds, 
    subscriptionId: `questions_${questionIds.join('_')}` 
  });
};

// Экспортируем типы для обратной совместимости
export type {
  QuestionStatusChangedEvent,
  QuestionMemberEvent,
  CommentEvent,
  CommentDeletedEvent,
  SessionEvent,
} from '../services/signalRManager';
